import * as React from 'react'
import {Drawer, Box, Typography, IconButton, Button, Chip} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {primaryBtnSx, secondaryBtnSx, alertStripSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE, BORDER, SUCCESS, PURPLE} from '../../../../styles/foundation/colors'
import {GAP} from '../../../../styles/foundation/spacing'
import {FREEZE_LEVEL_LABEL, STATUS_TONE} from '../data/reference'
import {date, int} from '../format'
import {isPreliminary, PRELIM_CYCLES, packOf} from './logic'
import {PRELIM_HELP} from './copy'
import {chipSentenceSx} from '../components/tone'
import KeyValue from '../components/KeyValue'
import RqLevelChip from './RqLevelChip'
import RqSection from './RqSection'
import RqWeights from './RqWeights'
import RqPackBlock from './RqPackBlock'
import RqTimeline from './RqTimeline'

const labelSx = {fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}

/**
 * Weight Freeze (v2) — Requests: the approved record drawer.
 *
 * Wider than the sibling Disputes drawer (660 vs 480) because this one
 * carries the weights triple, the packing block's carton drawing and the
 * full approval timeline. `req` null closes/blanks the drawer — the caller
 * (RqApproved) only ever mounts this with `open` following `req !== null`.
 */
export default function RqApprovedDrawer({req, packs, open, onClose, onNavigate}) {
  if (!req) return null

  const pack = packOf(packs, req.packId)
  const prelim = isPreliminary(req)
  const evidenceAwbs = req.evidenceAwbs || []
  const covers = req.covers || []

  const navigate = (section) => {
    onClose()
    onNavigate(section)
  }

  const record = [
    {label: 'Request', value: req.id},
    {label: 'Level', value: FREEZE_LEVEL_LABEL[req.level] || req.level},
    {label: 'Frozen for', value: req.subject},
    {label: 'Raised by', value: req.raisedBy},
    {label: 'Raised on', value: date(req.raisedOn)},
    {label: 'Approved by', value: req.approvedBy},
    {label: 'Effective from', value: date(req.effectiveFrom)},
    {label: 'Billing cycles elapsed', value: int(req.cyclesElapsed || 0)},
    {label: 'DWS images used', value: int(req.evidence || 0)},
  ]

  return (
    <Drawer anchor='right' open={open} onClose={onClose} sx={{'& .MuiDrawer-paper': {width: {xs: '100%', sm: 660}}}}>
      <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
        <Box sx={{p: 2, borderBottom: `1px solid ${BORDER}`}}>
          <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1}}>
            <Box sx={{minWidth: 0}}>
              <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.md, color: NAVY}}>
                {req.subject}
              </Typography>
              <Typography sx={labelSx}>
                {req.id} · {FREEZE_LEVEL_LABEL[req.level] || req.level} level · approved weight freeze
              </Typography>
            </Box>
            <IconButton onClick={onClose} aria-label='Close approved request drawer' size='small'>
              <CloseIcon fontSize='small' />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{p: 2, overflowY: 'auto', flex: 1, display: 'grid', gap: GAP.md, alignContent: 'start'}}>
          <Box sx={{display: 'flex', gap: 0.75, flexWrap: 'wrap'}}>
            <Chip size='small' label='Approved' sx={chipSentenceSx(STATUS_TONE.approved)} />
            <RqLevelChip level={req.level} />
            <Chip
              size='small'
              label={prelim ? 'Preliminary' : 'Fully in force'}
              sx={chipSentenceSx(prelim ? STATUS_TONE.preliminary : STATUS_TONE.approved)}
            />
          </Box>

          {prelim ? (
            <Box sx={{...alertStripSx(PURPLE), alignItems: 'flex-start'}}>
              <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, lineHeight: 1.6, fontWeight: WEIGHT.regular}}>
                <Box component='span' sx={{fontWeight: WEIGHT.bold}}>
                  Preliminary, cycle {int(req.cyclesElapsed || 0)} of {PRELIM_CYCLES}.
                </Box>{' '}{PRELIM_HELP}
              </Typography>
            </Box>
          ) : (
            <Box sx={{...alertStripSx(SUCCESS), alignItems: 'flex-start'}}>
              <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, lineHeight: 1.6, fontWeight: WEIGHT.regular}}>
                <Box component='span' sx={{fontWeight: WEIGHT.bold}}>Fully in force.</Box>{' '}
                {int(req.cyclesElapsed || 0)} billing cycles have run since the effective date, so the
                preliminary period is over. The frozen weight now bills as agreed, with no cycle-end
                reconciliation attached to it, for every shipment that goes out in the declared packing material.
              </Typography>
            </Box>
          )}

          {/* No `sectionCardSx` wrapper any more — RqWeights draws its own three
              tiles, and a card around them was a border inside a border. */}
          <RqSection title='Shipment weight'>
            <RqWeights req={req} />
          </RqSection>

          {/* RqPackBlock renders nothing without a pack, and a request raised before packing material
              was captured has none — which left a bare "Packing material" heading with a gap under it. */}
          {pack && (
            <RqSection title='Packing material'>
              <RqPackBlock pack={pack} condition />
            </RqSection>
          )}

          <RqSection title='Record'>
            <KeyValue items={record} />
          </RqSection>

          {evidenceAwbs.length > 0 && (
            <RqSection title='Attached shipment records'>
              <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5}}>
                {evidenceAwbs.map((awb) => (
                  <Chip key={awb} size='small' label={awb} sx={{fontFamily: 'monospace', fontSize: '0.65rem'}} />
                ))}
              </Box>
              <Typography sx={labelSx}>
                These are the shipments whose DWS records were attached when the request was raised.
              </Typography>
            </RqSection>
          )}

          {covers.length > 1 && (
            <RqSection title='Rows this freeze covers'>
              <Box component='ul' sx={{m: 0, pl: 2.5, display: 'grid', gap: 0.25}}>
                {covers.map((c) => (
                  <Typography key={c} component='li' sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY}}>
                    {c}
                  </Typography>
                ))}
              </Box>
            </RqSection>
          )}

          {req.remarks && (
            <RqSection title='Remarks'>
              <Typography sx={{fontFamily: FONT, fontSize: SIZE.xs, color: NAVY, lineHeight: 1.6}}>
                {req.remarks}
              </Typography>
            </RqSection>
          )}

          <RqTimeline history={req.history} round={1} label='Approval history' />
        </Box>

        <Box sx={{p: 1.5, borderTop: `1px solid ${BORDER}`, display: 'flex', gap: 1, justifyContent: 'space-between', flexWrap: 'wrap'}}>
          <Box sx={{display: 'flex', gap: 1}}>
            <Button sx={secondaryBtnSx} onClick={() => navigate('packing')}>Sub-categories and packing</Button>
          </Box>
          <Button sx={primaryBtnSx} onClick={onClose}>Close</Button>
        </Box>
      </Box>
    </Drawer>
  )
}
